import { Clock, Headset, Lock } from 'lucide-react';
import PageHero from '../components/sections/PageHero';
import PickupRequestForm from '../components/forms/PickupRequestForm';
import Container from '../components/ui/Container';
import Reveal from '../components/ui/Reveal';
import Icon from '../components/ui/Icon';
import { processSteps } from '../data/process';
import { site } from '../data/site';

const assurances = [
  { icon: Clock, label: 'Quotation within 24 hours' },
  { icon: Lock, label: 'NDA signed before pickup' },
  { icon: Headset, label: 'Named compliance manager' },
];

export default function RequestPickup() {
  return (
    <>
      <PageHero
        breadcrumb="Request Pickup"
        eyebrow="Request a pickup"
        title="Tell us what you have — we will handle the rest"
        description="Share your inventory details below. There is no obligation, and the quotation includes a category-wise rate card before you commit."
      />

      <section className="section bg-surface">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr] lg:items-start lg:gap-10">
            <Reveal direction="right">
              <PickupRequestForm />
            </Reveal>

            <Reveal direction="left" delay={0.1} className="lg:sticky lg:top-28">
              <div className="space-y-6">
                {/* What happens next */}
                <div className="rounded-xl2 border border-ink-100 bg-white p-7 shadow-soft">
                  <h2 className="font-display text-base font-semibold text-ink-900">
                    What happens next
                  </h2>

                  <ol className="mt-6 space-y-5">
                    {processSteps.slice(0, 4).map((step, index) => (
                      <li key={step.id} className="relative flex gap-4">
                        {index < 3 && (
                          <span
                            className="absolute left-[18px] top-10 h-[calc(100%-0.5rem)] w-px bg-ink-100"
                            aria-hidden="true"
                          />
                        )}
                        <span className="relative grid h-9 w-9 flex-none place-items-center rounded-full bg-brand-50 text-brand-700">
                          <Icon name={step.icon} className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 pb-1">
                          <p className="text-sm font-semibold text-ink-900">{step.title}</p>
                          <p className="mt-0.5 text-xs text-brand-700">{step.duration}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Assurances */}
                <div className="rounded-xl2 border border-brand-200 bg-brand-50/70 p-7">
                  <h2 className="font-display text-base font-semibold text-ink-900">
                    What you can count on
                  </h2>
                  <ul className="mt-5 space-y-3.5">
                    {assurances.map((item) => (
                      <li key={item.label} className="flex items-center gap-3 text-sm text-ink-700">
                        <item.icon className="h-4 w-4 flex-none text-brand-600" />
                        {item.label}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Direct contact */}
                <div className="rounded-xl2 border border-ink-100 bg-white p-7 shadow-soft">
                  <h2 className="font-display text-base font-semibold text-ink-900">
                    Prefer to talk it through?
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-ink-500">
                    For large lots, multi-site programmes or urgent shutdowns, call our enterprise desk
                    directly.
                  </p>
                  <div className="mt-5 space-y-2 text-sm">
                    <a
                      href={`tel:${site.phone.replace(/\s/g, '')}`}
                      className="block font-semibold text-brand-700 hover:underline"
                    >
                      {site.phone}
                    </a>
                    <a href={`mailto:${site.email}`} className="block text-ink-600 hover:text-brand-700">
                      {site.email}
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
    </>
  );
}
